/**
 * JotRx Encounter Action Hook
 *
 * Shared business logic for JotRx encounter CTAs.
 * Handles setup state, patient connection checks, DoseSpot SSO,
 * connect modal state, and patient demographics fetching.
 */

import { type MouseEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessToken } from '@features/user';
import APIService from '@services/APIService';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useEPrescriptionStatus, useLazyGetPatientSsoQuery } from '../store';

import { useJotRxSetupState } from './useJotRxSetupState';
import { useProxyOnBehalfOf } from './useProxyOnBehalfOf';

interface PatientAddressLike {
  line1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

interface PatientDemographics {
  first_name?: string;
  last_name?: string;
  dob?: string;
  sex?: string;
  gender?: string;
  mobile_phone?: string;
  phone?: string;
  primary_address?: PatientAddressLike | null;
  // Legacy flat address fields, kept as fallback
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface UseJotRxEncounterActionOptions {
  patientId?: string | null;
}

export function useJotRxEncounterAction({ patientId }: UseJotRxEncounterActionOptions) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { jotrx } = useEventTracking();
  const { setupState, isJotRxEnabled, isJotRxPurchasable, isLoading, cta, isFullySetup } = useJotRxSetupState();
  const { isPatientConnected, isLoading: isPatientStatusLoading, refetch: refetchStatus } = useEPrescriptionStatus({
    patientId: patientId || undefined,
    skip: !patientId,
  });
  const { getAccessToken } = useAccessToken();
  const [getPatientSso] = useLazyGetPatientSsoQuery();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [patientDemographics, setPatientDemographics] = useState<PatientDemographics | null>(null);

  const { isProxy, prescribers } = useProxyOnBehalfOf();
  const [proxyMenuAnchor, setProxyMenuAnchor] = useState<null | HTMLElement>(null);

  // Proxies have no personal JotRx entitlement; the org being connected to
  // DoseSpot is what gates them (enforced by setupState + backend).
  const isVisible =
    (isJotRxEnabled || isJotRxPurchasable || isProxy) && !isLoading;

  // Fetch patient demographics for connect modal pre-fill
  useEffect(() => {
    if (!patientId) {
      setPatientDemographics(null);
      return;
    }
    let cancelled = false;
    const fetchPatientData = async () => {
      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIGetRequest({
          requestString: `/patient/get?patient_id=${patientId}&origin=JotRxConnect`,
          accessToken,
        });
        if (response.ok && !cancelled) {
          const data = (await response.value) as { patient?: PatientDemographics } | undefined;
          setPatientDemographics(data?.patient || null);
        }
      } catch {
        // Non-critical — modal still works without pre-fill
      }
    };
    void fetchPatientData();
    return () => { cancelled = true; };
  }, [patientId, getAccessToken]);

  const openPatientSso = useCallback(async (onBehalfOfProviderId?: string) => {
    if (!patientId) return;
    const ssoWindow = window.open('about:blank', '_blank');
    if (!ssoWindow) {
      enqueueSnackbar('Popup blocked — please allow popups for this site.', { variant: 'warning' });
      return;
    }
    try {
      const result = await getPatientSso({
        patientId,
        ...(onBehalfOfProviderId ? { onBehalfOfProviderId } : {}),
      }).unwrap();
      if (result.sso_url) {
        ssoWindow.opener = null;
        ssoWindow.location.href = result.sso_url;
        jotrx.prescribeOpened({ source: 'encounter_cta', patient_id: patientId });
      } else {
        ssoWindow.close();
      }
    } catch {
      ssoWindow.close();
      enqueueSnackbar('Failed to open DoseSpot. Please try again.', { variant: 'error' });
    }
  }, [patientId, getPatientSso, enqueueSnackbar, jotrx]);

  const handleClick = useCallback(async (e?: MouseEvent<HTMLElement>) => {
    jotrx.setupStarted({ source: 'encounter_cta', setup_state: setupState });

    if (setupState === 'fully_setup' && patientId) {
      if (!isPatientConnected) {
        setShowConnectModal(true);
        return;
      }
      if (isProxy) {
        // GDN-1119: proxy must act on behalf of a connected prescriber.
        if (prescribers.length === 0) {
          enqueueSnackbar(
            'A prescriber in your organization must finish DoseSpot setup ' +
              'before you can prepare prescriptions on their behalf.',
            { variant: 'warning' },
          );
          return;
        }
        if (prescribers.length === 1) {
          await openPatientSso(prescribers[0].userId);
        } else if (e) {
          setProxyMenuAnchor(e.currentTarget);
        }
        return;
      }
      await openPatientSso();
      return;
    }

    navigate(cta.route);
  }, [
    jotrx,
    setupState,
    patientId,
    isPatientConnected,
    isProxy,
    prescribers,
    enqueueSnackbar,
    openPatientSso,
    cta.route,
    navigate,
  ]);

  const handleConnectSuccess = useCallback(async () => {
    refetchStatus();
    if (isProxy) {
      if (prescribers.length === 1) await openPatientSso(prescribers[0].userId);
    } else {
      await openPatientSso();
    }
  }, [refetchStatus, isProxy, prescribers, openPatientSso]);

  const selectProxyPrescriber = useCallback(
    (userId: string) => {
      setProxyMenuAnchor(null);
      void openPatientSso(userId);
    },
    [openPatientSso],
  );

  const connectModalPatientData = patientDemographics
    ? {
        firstName: patientDemographics.first_name,
        lastName: patientDemographics.last_name,
        dob: patientDemographics.dob || undefined,
        gender: patientDemographics.sex || patientDemographics.gender,
        phone: patientDemographics.mobile_phone || patientDemographics.phone,
        address: patientDemographics.primary_address?.line1 || patientDemographics.address,
        city: patientDemographics.primary_address?.city || patientDemographics.city,
        state: patientDemographics.primary_address?.state || patientDemographics.state,
        zipCode: patientDemographics.primary_address?.zip || patientDemographics.zip,
      }
    : undefined;

  return {
    setupState,
    isVisible,
    isFullySetup,
    isJotRxPurchasable,
    isPatientConnected,
    isPatientStatusLoading,
    isLoading,
    cta,
    handleClick,
    showConnectModal,
    setShowConnectModal,
    handleConnectSuccess,
    connectModalPatientData,
    // GDN-1119 proxy → prescriber picker (only when >1 connected prescriber)
    proxyPrescribers: prescribers,
    proxyMenuAnchor,
    closeProxyMenu: () => setProxyMenuAnchor(null),
    selectProxyPrescriber,
  };
}
